// Phase 36 §21b — the decisions a table makes, separated from its markup.
//
// These are here rather than inline because each one has a wrong answer that
// looks right on the screen you are building and is wrong on the next one:
// a header checkbox that never goes indeterminate, a "select all" that counts
// the loaded page, a sort arrow on a column the rows are not ordered by.

/** What a header checkbox shows for the rows currently on screen. */
export type HeaderCheck = 'none' | 'some' | 'all';

/**
 * Rest, partial, or full.
 *
 * The partial state is the one that gets skipped. Without it a header checkbox
 * on a page where three of forty rows are ticked renders as empty, and clicking
 * it looks like "select all" when the control is actually toggling.
 *
 * An empty page is `none`, never `all` — vacuous truth reads as "everything is
 * selected" to someone glancing at a tick.
 */
export function headerCheck(selected: number, onPage: number): HeaderCheck {
  if (onPage <= 0 || selected <= 0) return 'none';
  return selected >= onPage ? 'all' : 'some';
}

/**
 * The "select all" affordance, or null when it would be a lie.
 *
 * The count is the SERVER total, not the loaded page: offering "select all 25"
 * on the first page of 340 results and then acting on 340 — or on 25 — is the
 * same bug wearing two different labels. It renders only when there is more
 * beyond the page, because on a single page the header checkbox already did it.
 */
export function selectAllLabel(onPage: number, serverTotal: number): string | null {
  if (!Number.isFinite(serverTotal) || serverTotal <= onPage) return null;
  return `Select all ${serverTotal.toLocaleString()}`;
}

export type SortDirection = 'asc' | 'desc';

/**
 * The arrow for one column header.
 *
 * Null on every column except the one the rows are actually ordered by. A strip
 * of grey arrows on every heading says "sortable" and says nothing about the
 * order the reader is looking at, which is the only question the header can
 * answer that the rows cannot.
 */
export function sortArrow(
  column: string,
  activeColumn: string | null | undefined,
  direction: SortDirection = 'desc',
): string | null {
  if (!activeColumn || column !== activeColumn) return null;
  return direction === 'asc' ? '↑' : '↓';
}

/**
 * The foot line: what is shown, out of what.
 *
 * "All 4 shown" rather than a pager, because a pager under four rows is a
 * control that cannot be used. Above the page size it states both numbers so
 * the reader knows the list is truncated without counting rows.
 */
export function shownLabel(onPage: number, serverTotal: number): string {
  if (!Number.isFinite(serverTotal) || serverTotal <= onPage) {
    return `All ${onPage.toLocaleString()} shown`;
  }
  return `${onPage.toLocaleString()} of ${serverTotal.toLocaleString()} shown`;
}
